import * as THREE from 'three';
import { ringBirth } from './ringGroup';
import { CARD_GAP } from './cardLanding';

// The film strip a planet's artwork hangs in.
//
// Item 4 of the brief asked for "a film-strip-style background behind the image carousels", and
// the reference it came with is a 35mm strip: a black band, sprocket perforations along both
// edges, and light-framed windows. So the strip is not a backdrop the cards stand in front of —
// it is the thing the cards hang *in*, and it wraps the ring.
//
// The cards are already a ring: `resetImagePositions` puts them on a circle of RING_RADIUS
// planet radii about their group's origin, one per artwork, at equal angles. This module adds one
// open cylinder just outside that circle, carrying a generated texture whose frame windows fall
// on those same angles, so each artwork shows through its own window and the band around it is
// the frame.
//
// Three numbers have to agree for that to hold, and each is computed in exactly one place:
//
//   * an angle — `frameCentreU`, matching the expression `resetImagePositions` places cards with;
//   * a size — `windowFit`, which lands a card inside its window: contain, never crop or stretch;
//   * a pitch — `stripLayout`, which shrinks the frames on a crowded ring rather than letting
//     them overlap each other.
//
// All three are pure, and filmStrip.test.js pins them. The drawing is covered as well, through a
// context that records the rectangles it was asked for instead of rasterising them — jsdom hands
// out no 2D context, but nothing here reads one back, so a recorder tests the drawing exactly as
// far as it can be tested: the band, one window per frame on its card's angle, the perforations
// between them. The mesh is what is left untested; there is no scene in jsdom.
//
// Nothing here reads a card, and that is the point: the band is built from a count and a radius,
// both known before a single texture is asked for, so the strip is on the ring *before* the
// artwork it frames. What used to make it wait was the hole cut through the band where each card
// lands, which is what the cards needed while the band was drawn over them — and the strip was
// built from `LoadingManager.onLoad`, seconds behind the art, because a hole cannot be cut for a
// card that has not arrived. With the band painted first and the cards over it (see
// `addFilmStrip`), a hole is covered by the card standing in it, and the letterbox a frame shows
// is band fill — the same black either way. So there are no holes, the band depends on nothing,
// and a window cut for a card that never arrives is an empty frame rather than a hole too wide
// for its artwork, which is the one size that would have shown.

/**
 * The ring's radius, in planet radii — the circle `resetImagePositions` hangs the cards on.
 *
 * One radius for every ring, whatever it carries, so the strip hugs its planet at the same
 * distance everywhere. It lives here rather than in GlobalFunctions because the band has to be
 * placed relative to it: a band whose radius drifted from the ring's would put every window a
 * little off its card.
 */
export const RING_RADIUS = 1.1;

/** How far outside the ring the band sits, in planet radii. Enough to clear the cards, no more. */
export const BAND_OUTSIDE = 0.02;

/** The frame's shape, as the reference strip's frames are: wider than they are tall. */
export const WINDOW_ASPECT = 1.5;

/** The window's height on a roomy ring, in planet radii. */
export const PREFERRED_HEIGHT = 0.3;

/** The most of a card's arc a frame may take up. Below 1 so neighbouring frames cannot touch. */
export const ARC_FILL = 0.78;

/**
 * The arc one frame asks for, in planet radii of ring per work.
 *
 * It is the exact point at which a frame's want and its arc's allowance meet: a frame at the
 * preferred size is `WINDOW_ASPECT * PREFERRED_HEIGHT` radii wide, `ARC_FILL` is the share of its
 * arc it may fill, so a work needs that much circumference/`ARC_FILL`. Below this pitch the ring
 * seats its works at the preferred frame size; above it the frames come out narrower, and
 * `ringZoomFor` below is how far the camera may then close on them.
 *
 * Derived rather than written down, because a number copied out of this arithmetic is a number
 * that stops agreeing with the constants above it the first time one of them is tuned.
 */
export const RING_PITCH = (WINDOW_ASPECT * PREFERRED_HEIGHT) / (ARC_FILL * 2 * Math.PI);

/** Perforation and border room above and below the window, as a fraction of the window's height. */
export const PERF_MARGIN = 0.18;

/** The strip texture's width in pixels. The band is R-relative, so this is one size everywhere. */
export const TEXTURE_WIDTH = 2048;

export const BAND_COLOUR = '#0a0a0c';
const WINDOW_EDGE = 'rgba(150,150,162,0.55)';
const PERF_COLOUR = 'rgba(190,190,200,0.5)';
const RIM_COLOUR = 'rgba(200,200,214,0.22)';
const NUMBER_COLOUR = 'rgba(160,160,172,0.6)';

/** Perforations per frame. The reference has three; it is what makes the pitch read as film. */
const PERFS_PER_FRAME = 3;

/**
 * The band and the frames for one ring, from how many artworks it holds and how big the planet is.
 *
 * The frames are one size on every ring — a strip's frames are uniform — so a crowded ring gets
 * *smaller* frames rather than a bigger ring. That is the whole of what "it runs too wide, it
 * should fit the planet better" asked for: the ring used to grow until it could seat its works at
 * the preferred width, and on the gallery's most crowded ring that put a circle well clear of the
 * Sun it hung on. Height follows from the width rather than being set independently, which is what
 * keeps the shape constant; `PREFERRED_HEIGHT` is the width a roomy ring gives, and the arc is a
 * cap below it.
 *
 * The `Math.min` is the rule again, and the arc term is the one that bites: past `RING_PITCH` a
 * frame comes out as wide as its arc allows and no wider, so neighbouring frames cannot touch.
 * What that costs on screen is the camera's business — see `ringZoomFor`.
 *
 * An empty ring is answered rather than refused — `count: 0` gives `Infinity` of arc, so the
 * width falls back to the preferred size and nothing comes back NaN. Nothing is built for one.
 */
export function stripLayout({ count, radius }) {
    const bandRadius = radius * (RING_RADIUS + BAND_OUTSIDE);
    const arcPerCard = count > 0 ? (2 * Math.PI * bandRadius) / count : Infinity;
    const windowWidth = Math.min(WINDOW_ASPECT * radius * PREFERRED_HEIGHT, ARC_FILL * arcPerCard);
    const windowHeight = windowWidth / WINDOW_ASPECT;

    return {
        count,
        radius,
        bandRadius,
        windowWidth,
        windowHeight,
        arcPerCard,
        // The band is the window plus the room its perforations and edge highlight need.
        bandHeight: windowHeight * (1 + 2 * PERF_MARGIN),
    };
}

/** A ring's frame width in planet radii. One radius, because a strip's frames are sized in the
 *  planet's own radii — so the planet cancels out of anything measured in them. */
const windowWidthFor = count => stripLayout({ count, radius: 1 }).windowWidth;

/**
 * How far, in planet radii, the camera may close on a ring holding `count` works.
 *
 * One size is aimed at, and every ring is brought to it: the gallery's most crowded ring at full
 * zoom, which is the Sun's own zoomed view — 27 caricatures against 14 at the next ring along, so
 * the size is worth calling the Sun's. That size is the frames standing `CARD_GAP` in front of the
 * camera, which is where a clicked artwork rests and so the size the rest of the gallery shows a
 * work at.
 *
 * The arithmetic is one ratio. A frame is `windowWidth` wide and stands `distance` from the camera,
 * both in planet radii, so what it subtends is their ratio — and the planet cancels, because every
 * length in this scene is already a multiple of its own radius. Setting this ring's ratio to the
 * most crowded ring's is the whole of the expression below: a roomy ring, whose frames are already
 * most of the way there, stops a little nearer than it landed, and a crowded one stops a long way
 * in.
 *
 * **A ring that is not crowded at all still gets a zoom**, and that is the difference between this
 * and measuring a shortfall. A shortfall is zero on a roomy ring, and nothing multiplied by nothing
 * is nothing, so the control could never appear on one — which is what the rule before this did,
 * on the Sun and nowhere else.
 *
 * The camera's own stop falls out of the same expression instead of being clamped on from outside:
 * `countMax` is the gallery's largest ring, so no ring's frames are wider than its, no ring asks
 * for more than `CARD_GAP` of travel, and none can reach the ring it hangs on. Clamping it anyway
 * is what put the live Sun's camera *on* the ring — a frame of nothing but Sun — because a crowded
 * ring asked for more travel than the gap it had. See the note in MainScene.
 *
 * @param {number} count - works on this ring; a ring holding none has nothing to close on.
 * @param {number} countMax - works on the gallery's most crowded ring, which sets the size. Must be
 *   at least `count` — MainScene asks the gallery for its largest and passes that, so every ring it
 *   is asked about satisfies this. A `count` past it would ask to close further than the gap.
 * @param {number} landingRadii - where the camera lands, in planet radii.
 * @returns {number} radii of dolly available; 0 for a ring with nothing on it.
 */
export function ringZoomFor(count, countMax, landingRadii) {
    if (!(count > 0) || !(countMax > 0)) return 0;

    const narrowest = windowWidthFor(countMax);

    if (!(narrowest > 0)) return 0;

    return Math.max(0, landingRadii - RING_RADIUS - (CARD_GAP * windowWidthFor(count)) / narrowest);
}

/**
 * The size to draw a card at so that it sits *inside* a window, keeping its own shape.
 *
 * Contain, not cover: an artwork is never cropped and never stretched. What it does not fill is
 * the letterbox, and on this strip that is real black — the band is behind the artwork's rect
 * everywhere the rect does not reach.
 *
 * The returned number is the card mesh's scale, replacing the flat `0.3` that used to be written
 * by `resetImagePositions`, which is now its only caller: the strip's windows are drawn from the
 * layout alone, so an artwork is placed *inside* a frame rather than cut into one.
 */
export function windowFit(geometryWidth, geometryHeight, layout) {
    if (!(geometryWidth > 0) || !(geometryHeight > 0)) return 0;

    return Math.min(
        layout.windowWidth / geometryWidth,
        layout.windowHeight / geometryHeight,
    );
}

/**
 * Where frame `i`'s centre sits across the band, from 0 to 1 — and so which angle it is on.
 *
 * `u` is the cylinder's own coordinate, and three builds a cylinder's side at
 * `x = r sin(2*PI*u), z = r cos(2*PI*u)`. `resetImagePositions` places card `i` at
 * `x = rad sin(a), z = rad cos(a)` with `a = (i / count) * 2 * PI`. The two are the same
 * expression, so `u = i / count` is card `i`'s angle exactly — the offset is zero, and
 * filmStrip.test.js holds that rather than this comment asserting it.
 *
 * Note what that puts at u = 0: the *centre* of frame 0, which is also the cylinder's texture
 * seam. Half of frame 0 is therefore drawn at each end of the texture, which `drawStripTexture`
 * handles by drawing every frame twice — once shifted a full width right. Moving the cards half a
 * slot instead would put the seam in a gap, but it would also turn the ring's rest pose, which is
 * the artwork the viewer lands on being the one they see head-on.
 */
export function frameCentreU(count, i) {
    if (!(count > 0)) return 0;

    return (i % count) / count;
}

/**
 * Draws the strip: the black band, a light window edge per frame, the perforations, the numbers.
 *
 * The band is whole — nothing is cut out of it. What frames an artwork is the window edge drawn
 * over it plus the black the card does not cover, and the card is painted over the band (see
 * `addFilmStrip`), so a hole would sit under the artwork and never be seen: the letterbox reads
 * as black whether it comes from the band or from a hole with the sky behind it.
 *
 * Leaving the band whole is also what lets it be drawn before any artwork has loaded, which is
 * why the count is the only thing this is given: a hole is the one part of this drawing that
 * cannot be known until a card exists. One window per frame, on `frameCentreU`.
 */
export function drawStripTexture(layout, { numbers = true } = {}) {
    const circumference = 2 * Math.PI * layout.bandRadius;
    const heightPx = Math.max(64, Math.round((TEXTURE_WIDTH * layout.bandHeight) / circumference));
    const canvas = document.createElement('canvas');

    canvas.width = TEXTURE_WIDTH;
    canvas.height = heightPx;

    const ctx = canvas.getContext('2d');
    // Equal by construction, so the band is not stretched: the texture covers the band's whole
    // circumference across, and its whole height down.
    const pxPerUnit = TEXTURE_WIDTH / circumference;
    const midY = heightPx / 2;
    const windowW = layout.windowWidth * pxPerUnit;
    const windowH = layout.windowHeight * pxPerUnit;
    const margin = (heightPx - windowH) / 2;
    const slotPx = TEXTURE_WIDTH / layout.count;

    ctx.fillStyle = BAND_COLOUR;
    ctx.fillRect(0, 0, TEXTURE_WIDTH, heightPx);

    // A frame centred on u = 0 has half of itself at each end of the texture, so anything
    // horizontal is drawn twice. The second copy goes a full width to the *right*, because frame
    // 0 is the one rect that straddles an edge of the texture and it straddles the left one — the
    // half that falls off x = 0 is the half that has to reappear at the other end.
    //
    // A copy a width to the *left* is off-canvas for every frame at every count: the last frame's
    // right edge stops a whole gap short of the end, so nothing here ever runs past the right
    // edge to be caught by it. That is what this drew before, which is to say it drew nothing —
    // frame 0 reached the ring as half a window, its artwork cut down one side, with a black slot
    // where the other half should have been.
    const wrapped = draw => {
        draw(0);
        draw(TEXTURE_WIDTH);
    };

    for (let i = 0; i < layout.count; i++) {
        const centreX = frameCentreU(layout.count, i) * TEXTURE_WIDTH;

        wrapped(offset => {
            ctx.strokeStyle = WINDOW_EDGE;
            ctx.lineWidth = 2;
            ctx.strokeRect(
                centreX + offset - windowW / 2,
                midY - windowH / 2,
                windowW,
                windowH,
            );
        });

        if (!numbers) continue;

        ctx.fillStyle = NUMBER_COLOUR;
        ctx.font = `${Math.max(8, Math.round(margin * 0.6))}px monospace`;
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'left';
        wrapped(offset => {
            ctx.fillText(String(i + 1), centreX + offset - slotPx / 2 + margin * 0.45, margin * 0.5);
        });
    }

    // Sprocket perforations, along the top and bottom margins, at a pitch the frames set.
    ctx.fillStyle = PERF_COLOUR;
    const perfW = Math.min(slotPx / (PERFS_PER_FRAME * 2.2), margin * 1.1);
    const perfH = margin * 0.45;

    for (let i = 0; i < layout.count; i++) {
        const slotStart = frameCentreU(layout.count, i) * TEXTURE_WIDTH - slotPx / 2;

        for (let k = 0; k < PERFS_PER_FRAME; k++) {
            const x = slotStart + ((k + 0.5) / PERFS_PER_FRAME) * slotPx - perfW / 2;

            wrapped(offset => {
                ctx.fillRect(x + offset, margin / 2 - perfH / 2, perfW, perfH);
                ctx.fillRect(x + offset, heightPx - margin / 2 - perfH / 2, perfW, perfH);
            });
        }
    }

    // The faint highlight along the band's outer edges, which is what stops a black strip read
    // against a black sky from being no strip at all.
    ctx.strokeStyle = RIM_COLOUR;
    ctx.lineWidth = 1;
    wrapped(() => {
        ctx.beginPath();
        ctx.moveTo(-0.5, 0.5);
        ctx.lineTo(TEXTURE_WIDTH + 0.5, 0.5);
        ctx.moveTo(-0.5, heightPx - 0.5);
        ctx.lineTo(TEXTURE_WIDTH + 0.5, heightPx - 0.5);
        ctx.stroke();
    });

    const texture = new THREE.CanvasTexture(canvas);
    texture.colorSpace = THREE.SRGBColorSpace;

    return texture;
}

/**
 * Builds a planet's strip and hangs it on the ring, for `count` works.
 *
 * A child of `imgGroup`, not of the planet: the ring's origin is not the planet's — the group
 * sits `0.1 * radius` forward of it — and a band rotated about the wrong centre would put every
 * window a little off its card. Sharing the group also means the band and the cards are turned
 * about the same axis by `rotateRing`, which is what keeps them locked together as the ring
 * turns rather than being re-aligned each frame.
 *
 * `count` is how many works this ring is *for* — the gallery's own count for the category, not a
 * count of the cards that have arrived — which is what lets this be called before a single
 * texture is asked for. Nothing here reads a card, so there is nothing to wait for: the band, its
 * windows and its numbers are all known up front, and the strip is on the ring as the artwork
 * lands rather than seconds behind it. A texture that fails to load leaves its window empty, which
 * is the honest reading of it — the ring holds that many works and one of them is not here — and
 * the ring reserves the slot, so the cards that did arrive keep the angles their frames are drawn
 * at.
 *
 * May be called before or after `resetImagePositions` has placed the cards: that function walks
 * `imgGroup.children` filtering on `userData.isImage`, and the band is not one.
 */
export function addFilmStrip(imgGroup, count) {
    if (!(count > 0)) return null;

    const planet = imgGroup.parent;
    const layout = stripLayout({ count, radius: planet.userData.radius });

    const band = new THREE.Mesh(
        new THREE.CylinderGeometry(layout.bandRadius, layout.bandRadius, layout.bandHeight, 128, 1, true),
        new THREE.MeshBasicMaterial({
            map: drawStripTexture(layout),
            transparent: true,
            opacity: 0,
            // The near half, and it ignores the depth buffer.
            //
            // The band is a sleeve just outside the ring — its radius is RING_RADIUS + BAND_OUTSIDE
            // and the cards stand at RING_RADIUS — so *both* of its halves project onto the same
            // ellipse, and whichever half is drawn is the whole of what the camera sees of it. The
            // two halves fail in opposite ways, and each way has been reported:
            //
            //   * drawn on the near half with the depth test on, the band is a wall across the ring.
            //     A card shows through its own window only, and a window lines up with its card only
            //     when the ring is looked at down its own axis, which `rotateRing` sees to it is not:
            //     "images load fine without the film strip, then once the strip loads some images
            //     disappear" — with the vanished cards still opening their lightbox, because
            //     GlobalFunctions' raycast refuses only the cards the *planet* hides.
            //   * drawn on the far half, no card is lost, because every card is in front of it — but
            //     the far half is behind the planet, so the strip goes the way of the cards it stopped
            //     hiding: "film strip facing wrong direction (hidden behind planet from camera view)".
            //
            // What separates the two problems is depth, not radius — no radius fixes this, since a
            // concentric sleeve is always nearer than the cards on one arc and further on the other.
            // Every point of this band is outside the planet's sphere: the ring's centre sits
            // 0.1 * radius in front of the planet's, so the sleeve's nearest point is 1.05 * radius
            // from the planet's centre. So the near half *is* in front of the planet and may be
            // painted over it, and the far half is the one thing here the planet rightly hides.
            // With the depth test off the near half is drawn wherever it falls, planet or sky, and
            // nothing already in the buffer can refuse it. `depthWrite` is off for the other half of
            // the same idea: written depth is how the band would put a card out of sight, and the
            // cards it would cost are the far ones — the ones standing behind its own wall.
            //
            // With those two off, the band is paint, not furniture, and the ordering below is what
            // keeps it off the artwork.
            side: THREE.FrontSide,
            depthTest: false,
            depthWrite: false,
        }),
    );

    band.name = 'filmStrip';
    band.userData.isFilmStrip = true;
    // The traverses in MainScene fade a mesh by whose ring it belongs to, and everything else on
    // the ring carries its planet's name here. Without it the band falls to the branch that
    // hides *other* planets' furniture and fades out exactly as its own ring fades in.
    band.userData.group = planet.name;
    // Rendered — the camera enables layer 1 — and never raycast: all three raycasters in
    // GlobalFunctions set layer 0, so the band cannot be clicked or hovered through its windows.
    band.layers.set(1);
    // Painted *before* the cards — the reverse of the usual order, and the second half of the fix
    // above. A band with no depth test goes down wherever it falls, so the only thing that can keep
    // it off the artwork is getting there first: three sorts the transparent pass by this number
    // before anything else, so 0 is down before any card, and each card is given 1 in AddImages so
    // that it lands on top of its own frame. With the band last, every window edge and every inch of
    // letterbox it draws would sit over the work it frames.
    band.renderOrder = 0;

    imgGroup.add(band);
    imgGroup.userData.filmStrip = band;

    // Born at 0 in the material above, which is where a strip rests — then lit here if the camera
    // is already on this planet. The art arrives from the gallery's API, so this runs whenever
    // that fetch lands: on a slow one, after the visitor has already clicked, by which time the
    // traverses that fade a strip in have run — without this the band is built at 0 and stays
    // there while the cards around it come up.
    ringBirth(band);

    return band;
}
